import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendarDays,
  faChevronDown,
  faChevronRight,
  faFont,
  faICursor,
  faSignature,
} from "@fortawesome/free-solid-svg-icons";
import { SignatureField, SignatureType } from "../../../../../interfaces/file/fileSignature/SignatureBox";
import "./SignatureBoxesPanel.css"
import moment from "moment";
import AvatarImage from "../../../../ui/Avatar/AvatarImage";
import { RiDeleteBin5Line } from "react-icons/ri";

const Delete = RiDeleteBin5Line as any;

export interface PeoplePickerUser {
  userId: string;
  email?: string;
  fullName?: string;
  isParticipant?: boolean;
  avatarUrl?: string; // optional for future
}
interface SignatureBoxesPanelProps {
  activeNav: string | null;
  toggleNav: (nav: string) => void;
  groupedSignatureFields: { [key: number]: SignatureField[] };
  signatureFields: SignatureField[];
  selectedBoxId: string | null;
  toggleSelectedBox: (boxId: string) => void;
  selectedBox: SignatureField | undefined;
  updateSelectedBox: (updates: Partial<SignatureField>) => void;
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  showDropdown: boolean;
  setShowDropdown: (v: boolean) => void;
  suggestions: PeoplePickerUser[];
  handleSelect: (email: string) => void;
  isLoading: boolean;
}

const SignatureBoxesPanel: React.FC<SignatureBoxesPanelProps> = ({
  activeNav,
  toggleNav,
  groupedSignatureFields,
  signatureFields,
  selectedBoxId,
  toggleSelectedBox,
  selectedBox,
  updateSelectedBox,
  handleInputChange,
  showDropdown,
  setShowDropdown,
  suggestions,
  handleSelect,
  isLoading,
}) => {
  return (
    <div>
      <div
        className="top top_bar_flx border_tittle cursor-pointer"
        onClick={() => toggleNav("signature-boxes")}
      >
        <div className="top_bar_tittle">
          <h2>Signature Boxes</h2>
        </div>
        <FontAwesomeIcon
          className="text-inactive cursor-pointer"
          icon={
            activeNav === "signature-boxes" ? faChevronDown : faChevronRight
          }
        />
      </div>
      {activeNav === "signature-boxes" && (
        <div className="mb-3">
          {Object.keys(groupedSignatureFields)
            .sort((a, b) => parseInt(a) - parseInt(b))
            .map((pageNumberStr) => {
              const pageNumber = parseInt(pageNumberStr);
              const fieldsOnPage = groupedSignatureFields[pageNumber];
              return (
                <div key={pageNumber}>
                  <div className="flex items-center mt-4 mb-2">
                    <h4 className="flex-shrink-0 font-semibold text-xs text-muted mr-4">
                      Page {pageNumber}
                    </h4>
                    <div className="flex-grow border-b border-muted"></div>
                  </div>
                  {fieldsOnPage.map((field) => {
                    const isSigned = !!field.signatureValue;

                    return (
                      <div
                        key={field.id}
                        className={`mb-2 p-2 rounded cursor-pointer transition-colors ${field.id === selectedBoxId
                          ? "border border-purple-500 bg-purple-100"
                          : isSigned
                            ? "border border-green-200 bg-green-100"
                            : "border border-gray-200 hover:bg-gray-50"
                          }`}
                        onClick={() => toggleSelectedBox(field.id)}
                      >
                        {/* Top row */}
                        <div className="flex align-middle items-center gap-2">
                          {field.signatureType === SignatureType.DATE && (
                            <FontAwesomeIcon className="w-6" icon={faCalendarDays} />
                          )}
                          {field.signatureType === SignatureType.SIGNATURE && (
                            <FontAwesomeIcon className="w-6" icon={faSignature} />
                          )}
                          {field.signatureType === SignatureType.INITIALS && (
                            <FontAwesomeIcon className="w-6" icon={faFont} />
                          )}
                          {field.signatureType === SignatureType.TEXT && (
                            <FontAwesomeIcon className="w-6" icon={faICursor} />
                          )}

                          <div className="text_turcate font-medium">
                            {field.signerEmail || "(none)"}
                          </div>
                        </div>

                        {/* Status row */}
                        <div className="mt-1 text-xs flex items-center gap-1">
                          {isSigned ? (
                            <>
                              <span className="inline-block w-2 h-2 rounded-full bg-green-500" />
                              <span className="text-green-600">
                                Signed{" "}
                                {moment(field.signedAt).format("MM/DD/YYYY")}

                              </span>
                            </>
                          ) : (
                            <>
                              <span className="inline-block w-2 h-2 rounded-full bg-orange-500" />
                              <span className="text-orange-600">
                                Pending signature
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}

                </div>
              );
            })}
          {signatureFields.length === 0 && (
            <div className="stats">
              <p>
                There are currently no Signature Fields created. Right-click
                anywhere on the PDF to add a new field.
              </p>
            </div>
          )}
          {selectedBox && (
            <div>
              <div className="top border_tittle">
                <div className="top_bar_tittle">
                  <h2>Edit Box</h2>
                </div>
              </div>
              <div className="">
                <label>Signature Type</label>

                <div className="er_si_signature_type_group">
                  {/* Signature */}
                  <button
                    type="button"
                    className={`er_si_signature_btn ${selectedBox.signatureType === SignatureType.SIGNATURE ? "active" : ""
                      }`}
                    onClick={() =>
                      updateSelectedBox({ signatureType: SignatureType.SIGNATURE })
                    }
                  >
                    <FontAwesomeIcon icon={faSignature} />
                    <span>Signature</span>
                  </button>

                  {/* Initial */}
                  <button
                    type="button"
                    className={`er_si_signature_btn ${selectedBox.signatureType === SignatureType.INITIALS ? "active" : ""
                      }`}
                    onClick={() =>
                      updateSelectedBox({ signatureType: SignatureType.INITIALS })
                    }
                  >
                    <FontAwesomeIcon icon={faFont} />
                    <span>Initial</span>
                  </button>

                  {/* Date */}
                  <button
                    type="button"
                    className={`er_si_signature_btn ${selectedBox.signatureType === SignatureType.DATE ? "active" : ""
                      }`}
                    onClick={() =>
                      updateSelectedBox({ signatureType: SignatureType.DATE })
                    }
                  >
                    <FontAwesomeIcon icon={faCalendarDays} />
                    <span>Date</span>
                  </button>

                  {/* Text */}
                  <button
                    type="button"
                    className={`er_si_signature_btn ${selectedBox.signatureType === SignatureType.TEXT ? "active" : ""
                      }`}
                    onClick={() =>
                      updateSelectedBox({ signatureType: SignatureType.TEXT })
                    }
                  >
                    <FontAwesomeIcon icon={faICursor} />
                    <span>Text</span>
                  </button>
                </div>
                <p className="mb-2.5"></p>
                <small className="text-muted">
                  Click on document to place signature field
                </small>
              </div>

              {/* <div className="relative">
                <div className="custom_input_flled">
                  <label>Signer Email</label>
                  <input
                    type="email"
                    value={selectedBox.signerEmail || ""}
                    onChange={handleInputChange}
                  />
                  {showDropdown && suggestions.length > 0 && (
                    <ul className="signature_suggestion">
                      {suggestions.map((email, idx) => (
                        <li key={idx} onClick={() => handleSelect(email?.email)}>{email}</li>
                      ))}
                    </ul>
                  )}
                </div>
              </div> */}


              <div className="relative w-full">
                {/* Label */}
                <label className="mt-2.5 mb-1">
                  Signer Email
                </label>

                {/* Input */}
                <input
                  type="email"
                  value={selectedBox.signerEmail || ""}
                  onChange={handleInputChange}
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowDropdown(!showDropdown);
                  }}
                  placeholder="Search by name or email"
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm bg-[#F8F8F8] "
                  style={{
                    width: "100%",
                    outline: "none",
                    boxShadow: "none",
                  }}
                />

                {showDropdown && isLoading && (
                  <ul className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg"
                    style={{ paddingLeft: "0px" }}
                  >
                    {[1, 2, 3].map((_, idx) => (
                      <li key={idx} className="flex items-center gap-3 px-3 py-2 animate-pulse">
                        <div className="w-9 h-9 rounded-full bg-gray-300" />
                        <div className="flex-1 space-y-2">
                          <div className="h-3 w-1/2 bg-gray-300 rounded" />
                          <div className="h-2 w-3/4 bg-gray-200 rounded" />
                        </div>
                      </li>
                    ))}
                  </ul>
                )}

                {showDropdown && suggestions.length > 0 && (
                  <ul
                    className="absolute z-50 mt-1 w-full bg-white border border-gray-200 
                 rounded-md shadow-lg max-h-44 overflow-auto"
                    style={{ paddingLeft: "0px" }}
                  >
                    {suggestions.map((user, idx) => (
                      <li
                        key={idx}
                        onClick={() => handleSelect(user.email || "")}
                        className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-gray-100"
                      >
                        {/* Avatar / Initials */}
                        <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0">
                          <AvatarImage size="xxs" userId={user?.userId} />
                        </div>

                        {/* Text Content */}
                        <div className="flex-1">
                          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                            <span className="font-semibold text-gray-900 capitalize text-xs">
                              {user.fullName}
                            </span>

                            {/* Role Badge */}
                            {user.isParticipant ? (
                              <span className="text-[10px] px-2 py-0.5 rounded bg-blue-100 text-blue-700">
                                Participant
                              </span>
                            ) : (
                              <span className="text-[10px] px-2 py-0.5 rounded bg-purple-100 text-purple-700">
                                Employee
                              </span>
                            )}
                          </div>

                          <div className="text-[11px] text-gray-500 truncate mt-1">
                            {user.email}
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>



              {selectedBox && selectedBox.signerEmail && (
                <div className="er_si_added_signature_wrapper">
                  <label className="er_si_added_signature_label">
                    Added Signature
                  </label>

                  <div className="er_si_added_signature_card">
                    <div className="er_si_added_signature_left">
                      <span className="er_si_signature_dot" />

                      <div className="er_si_added_signature_text">
                        <span className="er_si_added_signature_type">
                          {selectedBox.signatureType === SignatureType.SIGNATURE && "Signature"}
                          {selectedBox.signatureType === SignatureType.INITIALS && "Initial"}
                          {selectedBox.signatureType === SignatureType.DATE && "Date"}
                          {selectedBox.signatureType === SignatureType.TEXT && "Text"}
                        </span>

                        <span className="er_si_added_signature_email break-all">
                          {selectedBox.signerEmail}
                        </span>
                      </div>
                    </div>

                    <button
                      type="button"
                      className="er_si_added_signature_delete"
                      onClick={() =>
                        updateSelectedBox({ signerEmail: "" })
                      }
                      title="Remove signature"
                    >
                      <Delete size={16} />
                    </button>
                  </div>
                </div>
              )}

            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SignatureBoxesPanel;
